import React, { useState, useEffect } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import { questionService } from '../../services/questionService';
import { protocolService } from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const ProgramAdminQuestions = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    text: '',
    type: 'mcq',
    choices: ['', '', '', ''],
    category: 'General'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userProtocols, setUserProtocols] = useState([]);
  const [userQuestions, setUserQuestions] = useState([]);
  const [selectedProtocols, setSelectedProtocols] = useState([]); // Add this state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load user's protocols and questions on component mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const [protocolsResponse, questionsResponse] = await Promise.all([
        protocolService.getProtocols({}),
        questionService.getQuestions({ per_page: 100 }) // Fetch up to 100 questions
      ]);

      setUserProtocols(protocolsResponse.protocols || []);
      setUserQuestions(questionsResponse.questions || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChoiceChange = (index, value) => {
    const newChoices = [...formData.choices];
    newChoices[index] = value;
    setFormData(prev => ({
      ...prev,
      choices: newChoices
    }));
  };

  const addChoice = () => {
    if (formData.choices.length < 6) {
      setFormData(prev => ({
        ...prev,
        choices: [...prev.choices, '']
      }));
    }
  };

  const removeChoice = (index) => {
    if (formData.choices.length > 2) {
      const newChoices = formData.choices.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        choices: newChoices
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate choices for MCQ
      if (formData.type === 'mcq') {
        const validChoices = formData.choices.filter(choice => choice.trim());
        if (validChoices.length < 2) {
          setError('Please provide at least 2 choices for multiple choice questions.');
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare question data
      const questionData = {
        text: formData.text.trim(),
        type: formData.type,
        category: formData.category,
        protocol_ids: selectedProtocols, // <-- send as array
      };

      // Add choices for MCQ questions
      if (formData.type === 'mcq') {
        questionData.choices = formData.choices.filter(c => c.trim());
      }
      
      // Add choices for color scale questions
      if (formData.type === 'color_scale') {
        questionData.choices = ['red', 'orange', 'yellow', 'green'];
      }

      // Create question using API
      const response = await questionService.createQuestion(questionData);

      if (response.success) {
        // Show success notification
        setShowSuccess(true);
        setFormData({
          text: '',
          type: 'mcq',
          choices: ['', '', '', ''],
          category: 'General'
        });
        setSelectedProtocols([]); // Reset selected protocols

        // Reload user questions
        await loadUserData();

        // Hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to create question');
      }

    } catch (error) {
      console.error('Error creating question:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create question. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResearchLayout pageTitle="Manage Questions">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Question created successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>
            <strong>Error!</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        <div className="row">
          {/* Create Question Form */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <i className="fas fa-question-circle me-2"></i>
                  Create New Question
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <div className="col-md-8">
                      <label htmlFor="text" className="form-label fw-bold">
                        <i className="fas fa-edit me-2 text-info"></i>
                        Question Text *
                      </label>
                      <textarea
                        className="form-control"
                        id="text"
                        name="text"
                        rows="3"
                        value={formData.text}
                        onChange={handleInputChange}
                        placeholder="Enter your research question..."
                        required
                      ></textarea>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="category" className="form-label fw-bold">
                        <i className="fas fa-tag me-2 text-info"></i>
                        Category
                      </label>
                      <select
                        className="form-select"
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        <option value="General">General</option>
                        <option value="Medical History">Medical History</option>
                        <option value="Lifestyle">Lifestyle</option>
                        <option value="Mental Health">Mental Health</option>
                        <option value="Physical Health">Physical Health</option>
                        <option value="Demographics">Demographics</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-list me-2 text-info"></i>
                      Question Type *
                    </label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="mcq"
                          value="mcq"
                          checked={formData.type === 'mcq'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="mcq">
                          Multiple Choice
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="boolean"
                          value="boolean"
                          checked={formData.type === 'boolean'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="boolean">
                          True/False (Yes/No)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="text"
                          value="text"
                          checked={formData.type === 'text'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="text">
                          Text (Short Answer)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="range"
                          value="range"
                          checked={formData.type === 'range'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="range">
                          Range (1-100)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="color_scale"
                          value="color_scale"
                          checked={formData.type === 'color_scale'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="color_scale">
                          Color Scale
                        </label>
                      </div>
                    </div>
                  </div>

                  {formData.type === 'mcq' && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-list-ul me-2 text-info"></i>
                        Answer Choices *
                      </label>
                      {formData.choices.map((choice, index) => (
                        <div key={index} className="input-group mb-2">
                          <span className="input-group-text">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Choice ${index + 1}`}
                            value={choice}
                            onChange={(e) => handleChoiceChange(index, e.target.value)}
                          />
                          {formData.choices.length > 2 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeChoice(index)}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      ))}
                      {formData.choices.length < 6 && (
                        <button
                          type="button"
                          className="btn btn-outline-info btn-sm"
                          onClick={addChoice}
                        >
                          <i className="fas fa-plus me-2"></i>
                          Add Choice
                        </button>
                      )}
                    </div>
                  )}

                  {formData.type === 'text' && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-font me-2 text-info"></i>
                        Text Answer (maximum 20 words)
                      </label>
                      <div className="form-text">
                        Respondents will answer with a short text (maximum 20 words).
                      </div>
                    </div>
                  )}

                  {formData.type === 'range' && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-sliders-h me-2 text-info"></i>
                        Range Selection (1-100)
                      </label>
                      <div className="form-text">
                        Respondents will select a numeric value between 1 and 100 using a slider or input controls.
                      </div>
                    </div>
                  )}

                  {formData.type === 'color_scale' && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-palette me-2 text-info"></i>
                        Color Scale Selection
                      </label>
                      <div className="form-text mb-3">
                        Respondents will select from 4 color options: Red, Orange, Yellow, Green displayed as colored rectangles.
                      </div>
                      <div className="card bg-light">
                        <div className="card-body">
                          <p className="text-muted small mb-3">Preview of color options:</p>
                          <div className="d-flex gap-2 flex-wrap">
                            <div className="d-flex align-items-center justify-content-center"
                                 style={{
                                   backgroundColor: '#dc3545',
                                   color: 'white',
                                   padding: '10px 20px',
                                   borderRadius: '5px',
                                   fontWeight: 'bold',
                                   minWidth: '80px'
                                 }}>
                              Red
                            </div>
                            <div className="d-flex align-items-center justify-content-center"
                                 style={{
                                   backgroundColor: '#fd7e14',
                                   color: 'white',
                                   padding: '10px 20px',
                                   borderRadius: '5px',
                                   fontWeight: 'bold',
                                   minWidth: '80px'
                                 }}>
                              Orange
                            </div>
                            <div className="d-flex align-items-center justify-content-center"
                                 style={{
                                   backgroundColor: '#ffc107',
                                   color: 'black',
                                   padding: '10px 20px',
                                   borderRadius: '5px',
                                   fontWeight: 'bold',
                                   minWidth: '80px'
                                 }}>
                              Yellow
                            </div>
                            <div className="d-flex align-items-center justify-content-center"
                                 style={{
                                   backgroundColor: '#198754',
                                   color: 'white',
                                   padding: '10px 20px',
                                   borderRadius: '5px',
                                   fontWeight: 'bold',
                                   minWidth: '80px'
                                 }}>
                              Green
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-link me-2 text-info"></i>
                      Link to Protocol(s)
                    </label>
                    <select
                      className="form-select"
                      multiple
                      value={selectedProtocols}
                      onChange={e =>
                        setSelectedProtocols(Array.from(e.target.selectedOptions, opt => Number(opt.value)))
                      }
                    >
                      {userProtocols.map(protocol => (
                        <option key={protocol.id} value={protocol.id}>
                          {protocol.name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Hold Ctrl (Windows) or Cmd (Mac) to select multiple protocols.
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setFormData({
                        text: '',
                        type: 'mcq',
                        choices: ['', '', '', ''],
                        category: 'General'
                      })}
                      disabled={isSubmitting}
                    >
                      <i className="fas fa-undo me-2"></i>
                      Reset Form
                    </button>
                    
                    <button
                      type="submit"
                      className="btn btn-info btn-lg px-4"
                      disabled={isSubmitting || !formData.text.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Create Question
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Questions Summary */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-chart-pie me-2 text-info"></i>
                  Questions Summary
                </h6>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-info" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading questions...</p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-3">
                      <div className="h2 text-info">{userQuestions.length}</div>
                      <div className="text-muted">Total Questions Created</div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Multiple Choice</span>
                        <span className="badge bg-primary">
                          {userQuestions.filter(q => q.type === 'mcq').length}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>True/False</span>
                        <span className="badge bg-secondary">
                          {userQuestions.filter(q => q.type === 'boolean').length}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Text</span>
                        <span className="badge bg-success">
                          {userQuestions.filter(q => q.type === 'text').length}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Range</span>
                        <span className="badge bg-warning">
                          {userQuestions.filter(q => q.type === 'range').length}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Color Scale</span>
                        <span className="badge bg-info">
                          {userQuestions.filter(q => q.type === 'color_scale').length}
                        </span>
                      </div>
                    </div>

                    <div className="border-top pt-3">
                      <h6 className="text-muted mb-2">Categories</h6>
                      {['General', 'Medical History', 'Lifestyle', 'Mental Health', 'Physical Health'].map(category => {
                        const count = userQuestions.filter(q => q.category === category).length;
                        return count > 0 ? (
                          <div key={category} className="d-flex justify-content-between mb-1">
                            <small>{category}</small>
                            <span className="badge bg-info">{count}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* My Questions List */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2 text-info"></i>
                  My Created Questions ({userQuestions.length})
                </h6>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-info" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading questions...</p>
                  </div>
                ) : userQuestions.length > 0 ? (
                  <div className="row">
                    {userQuestions.map((question) => (
                      <div key={question.id} className="col-lg-6 mb-3">
                        <div className="card border border-info">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title text-info mb-1">
                                {question.text}
                              </h6>
                              <span className={`badge ${
                                question.type === 'mcq'
                                  ? 'bg-primary'
                                  : question.type === 'boolean'
                                  ? 'bg-secondary'
                                  : question.type === 'range'
                                  ? 'bg-warning'
                                  : question.type === 'color_scale'
                                  ? 'bg-info'
                                  : 'bg-success'
                              }`}>
                                {question.type === 'mcq'
                                  ? 'MCQ'
                                  : question.type === 'boolean'
                                  ? 'T/F'
                                  : question.type === 'range'
                                  ? 'Range'
                                  : question.type === 'color_scale'
                                  ? 'Color'
                                  : 'Text'}
                              </span>
                            </div>
                            
                            <div className="mb-2">
                              <small className="text-muted">
                                <i className="fas fa-tag me-1"></i>
                                {question.category}
                              </small>
                            </div>

                            {question.choices && question.choices.length > 0 && (
                              <div className="mb-2">
                                <small className="text-muted d-block mb-1">Choices:</small>
                                <div className="small">
                                  {question.choices.map((choice, idx) => (
                                    <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                                      {String.fromCharCode(65 + idx)}: {choice}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                {new Date(question.created_at).toLocaleDateString()}
                              </small>
                              {question.protocol_id ? (
                                <span className="badge bg-success">Linked</span>
                              ) : (
                                <span className="badge bg-warning">Unlinked</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Questions Created Yet</h6>
                    <p className="text-muted">Create your first research question using the form above.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminQuestions;